// apps/admin/src/components/composer/canvas-marquee.tsx
//
// P5 UI Polish W6 T5 — CanvasMarquee: rubber-band selection overlay for
// the composer canvas. Mirrors CalendarMarquee (W5 T1) but operates in
// canvas-pixel space (1920×1080) rather than client-relative coordinates.
//
// Drag a rectangle on empty canvas to select multiple zones.
// Shift+drag = additive (unions with current useMultiSelect state).
// Plain drag = replace selection.
//
// Marquee origin is gated to empty canvas: pointerdown on a zone div
// (data-testid starts with "layout-editor-zone-") or on a resize handle
// (data-testid starts with "resize-") is ignored so normal drag/resize
// is unaffected.

import { useEffect, useRef, useState } from 'react';
import { type Rect } from '../../lib/rects';

interface Props {
  containerRef: React.RefObject<HTMLElement>;
  enabled: boolean;
  canvasWidthPx: number;
  canvasHeightPx: number;
  onSelectionRect: (canvasRect: Rect, shift: boolean) => void;
}

export function CanvasMarquee({
  containerRef,
  enabled,
  canvasWidthPx,
  canvasHeightPx,
  onSelectionRect,
}: Props): JSX.Element | null {
  // Displayed rubber-band rect in canvas-pixel space.
  const [marqueeRect, setMarqueeRect] = useState<Rect | null>(null);

  // Refs to track drag state without re-registering event listeners on every
  // pointermove (which would break fast drags, same pattern as CalendarMarquee).
  const originRef = useRef<{ x: number; y: number; shift: boolean } | null>(null);
  const currentRef = useRef<{ x: number; y: number } | null>(null);
  const activeRef = useRef(false);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const el = containerRef.current;

    /**
     * Convert client coordinates to canvas-pixel space.
     *
     * The inner canvas div is rendered at REFERENCE_WIDTH_PX × REFERENCE_HEIGHT_PX
     * and then CSS-scaled with `transform: scale(zoom); transform-origin: top left`.
     * getBoundingClientRect() returns the visually-scaled dimensions, so dividing
     * by that scale factor converts back to the canvas's intrinsic pixel space.
     */
    const clientToCanvas = (clientX: number, clientY: number): { x: number; y: number } => {
      const bounding = el.getBoundingClientRect();
      const scaleX = canvasWidthPx / bounding.width;
      const scaleY = canvasHeightPx / bounding.height;
      return {
        x: (clientX - bounding.left) * scaleX,
        y: (clientY - bounding.top) * scaleY,
      };
    };

    const onDown = (e: PointerEvent): void => {
      const target = e.target as HTMLElement;
      // Gate: do not start a marquee on a zone div or resize handle.
      if (target.closest('[data-testid^="layout-editor-zone-"]')) return;
      if (target.closest('[data-testid^="resize-"]')) return;

      const { x, y } = clientToCanvas(e.clientX, e.clientY);
      originRef.current = { x, y, shift: e.shiftKey };
      currentRef.current = { x, y };
      activeRef.current = true;
      setMarqueeRect({ x, y, w: 0, h: 0 });
    };

    const onMove = (e: PointerEvent): void => {
      if (!activeRef.current || !originRef.current) return;
      const { x: cx, y: cy } = clientToCanvas(e.clientX, e.clientY);
      currentRef.current = { x: cx, y: cy };
      const o = originRef.current;
      setMarqueeRect({
        x: Math.min(o.x, cx),
        y: Math.min(o.y, cy),
        w: Math.abs(cx - o.x),
        h: Math.abs(cy - o.y),
      });
    };

    const onUp = (): void => {
      if (!activeRef.current) return;
      const origin = originRef.current;
      const current = currentRef.current;
      if (origin && current) {
        const r: Rect = {
          x: Math.min(origin.x, current.x),
          y: Math.min(origin.y, current.y),
          w: Math.abs(current.x - origin.x),
          h: Math.abs(current.y - origin.y),
        };
        // Only emit if the user actually dragged (> 4px in each dimension) to
        // prevent accidental selection on a single click.
        if (r.w > 4 && r.h > 4) {
          onSelectionRect(r, origin.shift);
        }
      }
      originRef.current = null;
      currentRef.current = null;
      activeRef.current = false;
      setMarqueeRect(null);
    };

    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [enabled, containerRef, canvasWidthPx, canvasHeightPx, onSelectionRect]);

  if (!marqueeRect) return null;

  // The canvas div is natively 1920×1080 in pixel space. Render the marquee
  // with absolute pixel positions — the same coordinate system as ZoneDraggable.
  return (
    <div
      className="pointer-events-none absolute z-10 border-2 border-dashed border-amber-500 bg-amber-500/10"
      style={{
        left: marqueeRect.x,
        top: marqueeRect.y,
        width: marqueeRect.w,
        height: marqueeRect.h,
      }}
      data-testid="canvas-marquee"
    />
  );
}
